import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  BlockStack,
  Button,
  InlineStack,
  Text,
  List,
  Banner,
  Spinner,
  Toast,
  Box,
  Grid,
  Icon,
  Select
} from "@shopify/polaris";
import { CheckIcon, ArrowRightIcon } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { GetApiCall, ApiCall } from "../helper/axios";
import { BridgeApp, config_variable } from "../helper/commonApi";
import { Redirect } from "@shopify/app-bridge/actions";
import { useStatusCheck } from "../hooks/useStatusCheck";
import onboardingGif1 from "../assets/img/onboarding_1.gif";
import onboardingGif3 from "../assets/img/onboarding_3.gif";
import onboardingGif5 from "../assets/img/onboarding_5.png";

const ONBOARDING_STEPS = {
  WELCOME: 0,
  THEME_SELECTION: 1,
  SNIPPET_INSTALLATION: 2,
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { store_data } = useSelector((store) => store.commonData);
  const { clearCache } = useStatusCheck(); // Get clearCache function
  const token = store_data?.token;
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.WELCOME);
  const [themes, setThemes] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [installingSnippet, setInstallingSnippet] = useState(false);
  const [snippetSuccess, setSnippetSuccess] = useState(false);
  const [snippetError, setSnippetError] = useState(false);
  const [toast, setToast] = useState({ active: false, content: "", error: false });
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [nextBtnLoader, setNextBtnLoader] = useState(false);
  const [onLoadLoader, setOnLoadLoader] = useState(true);
  const [completeSetup, setCompleteSetup] = useState(true);

  const showToast = (content, error = false) => setToast({ active: true, content, error });

  const fetchOnboardingData = async () => {
    setOnLoadLoader(true);
    try {
      const res = await GetApiCall("GET", "/onboarding-flow", { authentication: token });
      if (res?.data?.status === "success") {
        const payload = res?.data?.data;
        // Backend sets steps === false when onboarding is already finished (incl. migrated 4-step completers)
        if (payload?.steps === false) {
          clearCache();
          if (process.env?.MODE !== "local" && BridgeApp) {
            const redirect = Redirect.create(BridgeApp);
            redirect.dispatch(Redirect.Action.APP, "/home");
          } else {
            navigate("/", { replace: true });
          }
          return;
        }
        if (payload?.steps) {
          let currentStep = payload?.currentStep;
          if (currentStep === 1) {
            fetchThemes();
          } else if (currentStep === 2) {
            setSelectedTheme(payload?.selectedTheme)
          }
          setCurrentStep(currentStep);
        }
      } else {
        // showToast("Something went wrong", true);
      }
    } catch (e) {
      setOnLoadLoader(false);
      // showToast("Something went wrong", true);
    } finally {
      setOnLoadLoader(false);
    }
  }

  // Fetch themes
  const fetchThemes = async () => {
    setLoadingThemes(true);
    try {
      const res = await GetApiCall("GET", "/themes/list", { authentication: token });
      if (res?.data?.status === "success") {
        setThemes(Array.isArray(res?.data?.data) ? res.data.data : []);
      } else {
        setThemes([]);
        showToast("Failed to fetch themes", true);
      }
    } catch (e) {
      setThemes([]);
      showToast("Failed to fetch themes", true);
    } finally {
      setLoadingThemes(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchOnboardingData();

      // fetchThemes();
    }
  }, [token]);

  useEffect(() => {
    if (currentStep === 1) {
      fetchThemes();
    }
  }, [currentStep]);

  // Handle theme enable - redirect to theme customization
  const handleEnableTheme = () => {
    if (!selectedThemeId) {
      showToast("Please select a theme first", true);
      return;
    }

    // Find the selected theme from the themes array
    const theme = themes.find(t => t.id === selectedThemeId);
    if (!theme) {
      showToast("Selected theme not found", true);
      return;
    }

    setSelectedTheme(theme);
    try {
      if (process.env?.MODE !== "local" && BridgeApp) {
        // Redirect to theme customization page for embed block
        const themeId = theme.id?.replace("gid://shopify/OnlineStoreTheme/", "");
        window.open(
          `https://${store_data?.shop_data?.store_name}/admin/themes/${themeId}/editor?context=apps&activateAppId=${config_variable?.config?.apiKey}/mg_dynamic-price`,
          "_blank", "noopener,noreferrer"
        )
      } else {
        showToast("Theme customization available in production mode", false);
      }
    } catch (error) {
      showToast("Failed to open theme customization", true);
    }
  };

  // Install snippet automatically
  const handleInstallSnippet = async () => {
    if (!selectedTheme) {
      showToast("Please select a theme first", true);
      return;
    }

    setInstallingSnippet(true);
    setSnippetError(false);
    setSnippetSuccess(false);

    try {
      const res = await ApiCall(
        "POST",
        "/theme/install-snippet",
        { themeId: selectedTheme.id },
        { authentication: token }
      );

      if (res?.data?.status === "success") {
        setSnippetSuccess(true);
        setCompleteSetup(false);
        showToast("Snippet installed successfully!");
      } else {
        setSnippetError(true);
        showToast("Failed to install snippet automatically", true);
      }
    } catch (e) {
      setSnippetError(true);
      showToast("Failed to install snippet automatically", true);
    } finally {
      setInstallingSnippet(false);
    }
  };

  const nextStep = async () => {
    setNextBtnLoader(true)
    if (currentStep === 1) {
      if (!selectedThemeId) {
        setNextBtnLoader(false);
        showToast("Please select a theme first", true);
        return;
      }

      try {
        const response = await ApiCall(
          "POST",
          "/onboarding-flow",
          { currentStep, selectedThemeId, selectedTheme },
          { authentication: token }
        );
        if (response?.data?.status === "success") {
          if (response?.data?.data?.embedded_block_enabled) {
            if (currentStep < ONBOARDING_STEPS.SNIPPET_INSTALLATION) {
              setCurrentStep(currentStep + 1);
            }
          } else {
            showToast("Please enable the app extension in the theme editor first", true);
          }
        } else {
          showToast("Something went wrong", true);
        }
      } catch (e) {
        setNextBtnLoader(false);
        showToast("Something went wrong", true);
      } finally {
        setNextBtnLoader(false);
      }
    } else if (currentStep === 2) {
      try {
        const response = await ApiCall(
          "POST",
          "/onboarding-flow",
          { currentStep },
          { authentication: token }
        );
        if (response?.data?.status === "success") {
          clearCache();

          setTimeout(() => {
            if (process.env?.MODE !== 'local' && BridgeApp) {
              const redirect = Redirect.create(BridgeApp);
              redirect.dispatch(Redirect.Action.APP, "/home");
            } else {
              navigate("/", { replace: true });
            }
          }, 500);
        } else {
          showToast("Something went wrong", true);
          setNextBtnLoader(false);
        }
      } catch (e) {
        setNextBtnLoader(false);
        showToast("Something went wrong", true);
      }
    } else if (currentStep === 0) {
      try {
        const response = await ApiCall(
          "POST",
          "/onboarding-flow",
          { currentStep },
          { authentication: token }
        );
        if (response?.data?.status === "success") {
          if (currentStep < ONBOARDING_STEPS.SNIPPET_INSTALLATION) {
            setCurrentStep(currentStep + 1);
          }
        } else {
          showToast("Something went wrong", true);
        }
      } catch (e) {
        setNextBtnLoader(false);
        showToast("Something went wrong", true);
      } finally {
        setNextBtnLoader(false);
      }

    }
  };

  const prevStep = () => {
    if (currentStep > ONBOARDING_STEPS.WELCOME) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render left side image placeholder
  const renderLeftImage = (stepIndex) => {
    const imageStyles = {
      width: "100%",
      height: "400px",
      backgroundColor: "#ffffff",
      backgroundImage: "none",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };

    const stepImages = {
      [ONBOARDING_STEPS.WELCOME]: onboardingGif1,
      [ONBOARDING_STEPS.THEME_SELECTION]: onboardingGif3,
      [ONBOARDING_STEPS.SNIPPET_INSTALLATION]: onboardingGif5,
    };

    const currentImage = stepImages[stepIndex];
    const isEmoji = false;

    return (
      <Box padding="400">
        <div style={imageStyles}>
          {isEmoji ? (
            <Text as="h1" variant="heading2xl" fontWeight="bold" alignment="center">
              <span style={{ fontSize: "120px" }}>{currentImage}</span>
            </Text>
          ) : (
            <img
              src={currentImage}
              alt={`Onboarding step ${stepIndex}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </div>
      </Box>
    );
  };

  // Step 0: Welcome Screen
  const renderWelcomeStep = () => (
    <Grid>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 5, lg: 5, xl: 5 }}>
        {renderLeftImage(ONBOARDING_STEPS.WELCOME)}
      </Grid.Cell>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 7, lg: 7, xl: 7 }}>
        <Box padding="600">
          <BlockStack gap="500">
            <Text as="h1" variant="heading2xl" fontWeight="bold">
              Welcome to Dynamic Pricing!
            </Text>
            <Text as="p" variant="bodyLg" tone="subdued">
              Get started with powerful dynamic pricing features for your Shopify store. Here's what you can do:
            </Text>
            <Card>
              <BlockStack gap="300">
                <List type="bullet">
                  <List.Item>
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      Create dynamic pricing rules
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Set up rules based on device type, time of day, customer segments, and more
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      Automatic price updates
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Prices update automatically based on your configured rules
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      Real-time analytics
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Track orders and pricing performance with detailed analytics
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      Flexible configuration
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Customize pricing logic with percentage or manual price adjustments
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      Multi-rule management
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Create and manage multiple pricing rules simultaneously
                    </Text>
                  </List.Item>
                </List>
              </BlockStack>
            </Card>
          </BlockStack>
        </Box>
      </Grid.Cell>
    </Grid>
  );

  // Step 1: Theme Selection
  const renderThemeSelectionStep = () => (
    <Grid>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 5, lg: 5, xl: 5 }}>
        {renderLeftImage(ONBOARDING_STEPS.THEME_SELECTION)}
      </Grid.Cell>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 7, lg: 7, xl: 7 }}>
        <Box padding="600">
          <BlockStack gap="500">
            <Text as="h1" variant="heading2xl" fontWeight="bold">
              Select and Enable Theme
            </Text>
            <Text as="p" variant="bodyLg" tone="subdued">
              Choose a theme to enable dynamic pricing. Click "Enable" to open the theme customization page where you can add the embed block.
            </Text>
            <Card>
              <BlockStack gap="400">
                {loadingThemes ? (
                  <Box padding="600">
                    <InlineStack align="center">
                      <Spinner accessibilityLabel="Loading themes" size="large" />
                    </InlineStack>
                  </Box>
                ) : themes.length === 0 ? (
                  <Banner tone="warning">
                    <Text as="p">
                      No themes found. Please ensure your store has themes configured.
                    </Text>
                  </Banner>
                ) : (
                  <BlockStack gap="400">
                    <Select
                      label="Select Theme"
                      options={[
                        { label: "Choose a theme...", value: "" },
                        ...themes.map((theme) => ({
                          label: `${theme.name || "Unnamed Theme"}${theme.role === "MAIN" ? " (Main)" : ""}`,
                          value: theme.id,
                        })),
                      ]}
                      value={selectedThemeId}
                      onChange={setSelectedThemeId}
                    />
                    <InlineStack gap="300">
                      <Button
                        primary
                        onClick={handleEnableTheme}
                        disabled={!selectedThemeId}
                      >
                        Enable
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}
                <Banner tone="info">
                  <Text as="p" variant="bodySm">
                    After clicking "Enable", you'll be redirected to the theme editor. Look for the "App embeds" section and enable the dynamic pricing app block.
                  </Text>
                </Banner>
              </BlockStack>
            </Card>
          </BlockStack>
        </Box>
      </Grid.Cell>
    </Grid>
  );

  // Step 2: Snippet Installation
  const renderSnippetInstallationStep = () => (
    <Grid>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 5, lg: 5, xl: 5 }}>
        {renderLeftImage(ONBOARDING_STEPS.SNIPPET_INSTALLATION)}
      </Grid.Cell>
      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 7, lg: 7, xl: 7 }}>
        <Box padding="600">
          <BlockStack gap="500">
            <Text as="h1" variant="heading2xl" fontWeight="bold">
              Install Configuration to Theme
            </Text>
            <Text as="p" variant="bodyLg" tone="subdued">
              We'll automatically add the required configuration to your theme. If automatic installation fails, contact us for assistance.
            </Text>
            <Card>
              <BlockStack gap="400">
                {!snippetSuccess && !snippetError && (
                  <BlockStack gap="300">
                    <Text as="p" variant="bodyMd">
                      Click the button below to automatically install the configuration to your theme.
                    </Text>
                    <InlineStack gap="300">
                      <Button
                        primary
                        loading={installingSnippet}
                        onClick={handleInstallSnippet}
                        disabled={!selectedTheme || installingSnippet}
                      >
                        {installingSnippet ? "Installing..." : "Install Configuration Automatically"}
                      </Button>
                    </InlineStack>
                    {installingSnippet && (
                      <Banner tone="info">
                        <Text as="p" variant="bodyMd">
                          Please wait — installation is in progress. This often takes 1–2 minutes; do not refresh or leave this page.
                        </Text>
                      </Banner>
                    )}
                  </BlockStack>
                )}

                {snippetSuccess && (
                  <Banner tone="success">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Configuration installed successfully!
                      </Text>
                      <Text as="p" variant="bodySm">
                        Your theme has been updated with the dynamic pricing configuration. You're all set!
                      </Text>
                    </BlockStack>
                  </Banner>
                )}

                {snippetError && (
                  <BlockStack gap="400">
                    <Banner tone="critical">
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          Automatic installation failed
                        </Text>
                        <Text as="p" variant="bodySm">
                          We couldn't automatically install the configuration to your theme. Please contact us for assistance.
                        </Text>
                      </BlockStack>
                    </Banner>

                    <Card sectioned>
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          Contact Information
                        </Text>
                        <List type="bullet">
                          <List.Item>
                            <Text as="span" variant="bodyMd">
                              Email:{" "}
                              <Text as="span" fontWeight="semibold">
                              info@lumoonix.com
                              </Text>
                            </Text>
                          </List.Item>
                          <List.Item>
                            <Text as="span" variant="bodyMd">
                              Phone:{" "}
                              <Text as="span" fontWeight="semibold">
                                +91 7046941095
                              </Text>
                            </Text>
                          </List.Item>
                          <List.Item>
                            <Text as="span" variant="bodyMd">
                              Support Hours: Monday - Saturday, 9 AM - 10 PM IST
                            </Text>
                          </List.Item>
                        </List>
                      </BlockStack>
                    </Card>

                    <Card sectioned>
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          Manual Installation Instructions
                        </Text>
                        <Text as="p" variant="bodySm">
                          If you prefer to install manually, please contact our support team who will guide you through the process.
                        </Text>
                      </BlockStack>
                    </Card>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Box>
      </Grid.Cell>
    </Grid>
  );

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case ONBOARDING_STEPS.WELCOME:
        return renderWelcomeStep();
      case ONBOARDING_STEPS.THEME_SELECTION:
        return renderThemeSelectionStep();
      case ONBOARDING_STEPS.SNIPPET_INSTALLATION:
        return renderSnippetInstallationStep();
      default:
        return renderWelcomeStep();
    }
  };

  return (
    <Page
      title="Onboarding"
      primaryAction={
        currentStep === ONBOARDING_STEPS.SNIPPET_INSTALLATION
          ? {
            content: "Complete Setup",
            onAction: nextStep,
            disabled: completeSetup,
            loading: nextBtnLoader
          }
          : {
            content: "Next",
            icon: ArrowRightIcon,
            onAction: nextStep,
            loading: nextBtnLoader
          }
      }
      secondaryActions={
        currentStep > ONBOARDING_STEPS.WELCOME
          ? [
            {
              content: "Previous",
              onAction: prevStep,
            },
          ]
          : []
      }
    >
      {toast.active && (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast((t) => ({ ...t, active: false }))}
        />
      )}

      {onLoadLoader ?
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
        :
        <BlockStack gap="500">
          {/* Progress indicator */}
          <Card>
            <Box padding="500">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto',
              }}>
                {[0, 1, 2].map((stepIndex) => {
                  const isCompleted = stepIndex < currentStep;
                  const isCurrent = stepIndex === currentStep;

                  return (
                    <React.Fragment key={stepIndex}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: stepIndex === 2 ? '0 0 auto' : '1',
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                          zIndex: 2,
                        }}>
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: isCompleted || isCurrent ? '#000000' : '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: isCompleted || isCurrent ? '#ffffff' : '#000000',
                              fontWeight: '600',
                              fontSize: '14px',
                              transition: 'all 0.2s ease',
                              border: isCompleted || isCurrent ? 'none' : '2px solid #e1e3e5',
                              cursor: 'default',
                            }}
                          >
                            {isCompleted ? (
                              <span style={{ color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon source={CheckIcon} />
                              </span>
                            ) : (
                              <span style={{ lineHeight: 1 }}>{stepIndex + 1}</span>
                            )}
                          </div>
                        </div>
                        {stepIndex < 2 && (
                          <div
                            style={{
                              flex: '1',
                              height: '2px',
                              backgroundColor: isCompleted ? '#000000' : '#e1e3e5',
                              margin: '0 8px',
                              transition: 'background-color 0.2s ease',
                            }}
                          />
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </Box>
          </Card>

          {/* Step content */}
          <Card>{renderStepContent()}</Card>
        </BlockStack>
      }
    </Page>
  );
}
